import React from "react";
import _ from "lodash";
import { Link } from "react-router-dom";
import { parseISO } from "date-fns";

import { updateAttributesOnDocument } from "util/pouchActions";

import {
  getLifterAwardsWeightClassDoc,
  getScore,
  getTotal,
  getLastSuccessfulLiftTimestamp,
} from "./lifterHelper";

export const selectNextAttempt = function (knownLiftingOrder, meet, platform) {
  let nextAttempt = _.head(knownLiftingOrder);
  let nextAttemptId = _.get(nextAttempt, "attemptId");

  // usually the first attempt is already the current attempt
  // but we check to make sure since the user can override the current attempt.
  if (nextAttemptId === platform.currentAttemptId) {
    const nextLiftingOrder = _.drop(knownLiftingOrder);
    nextAttempt = _.head(nextLiftingOrder);
    nextAttemptId = _.get(nextAttempt, "attemptId");
  }

  if (_.isUndefined(nextAttemptId)) {
    nextAttemptId = null;
  }

  updateAttributesOnDocument(meet._id, platform._id, {
    currentAttemptId: nextAttemptId,
    clockState: "initial",
  });
};

export const powerliftingLifts = ["squat", "bench", "dead"];
export const powerliftingAttempts = ["1", "2", "3"]; // doesn't include 4th attempts, these are what we create initially and what count towards total

export const getEmptyPlatesKg = function () {
  return {
    plate_50: {
      pairsCount: 0,
      weight: 50,
      color: "#5CB85C",
    },
    plate_25: {
      pairsCount: 12,
      weight: 25,
      color: "#C25140",
    },
    plate_20: {
      pairsCount: 1,
      weight: 20,
      color: "#00008B",
    },
    plate_15: {
      pairsCount: 1,
      weight: 15,
      color: "#CCCC00",
    },
    plate_10: {
      pairsCount: 1,
      weight: 10,
      color: "#000000",
    },
    plate_5: {
      pairsCount: 1,
      weight: 5,
      color: "#000000",
    },
    plate_2_5: {
      pairsCount: 1,
      weight: 2.5,
      color: "#000000",
    },
    plate_2: {
      pairsCount: 0,
      weight: 2,
      color: "#000000",
    },
    plate_1_5: {
      pairsCount: 0,
      weight: 1.5,
      color: "#000000",
    },
    plate_1_25: {
      pairsCount: 1,
      weight: 1.25,
      color: "#000000",
    },
    plate_1: {
      pairsCount: 0,
      weight: 1,
      color: "#000000",
    },
    plate_0_5: {
      pairsCount: 2,
      weight: 0.5,
      color: "#000000",
    },
    plate_0_25: {
      pairsCount: 1,
      weight: 0.25,
      color: "#000000",
    },
  };
};

export const getEmptyPlatesLbs = function () {
  return {
    plate_100: {
      pairsCount: 0,
      weight: 100,
      color: "#5CB85C",
    },
    plate_55: {
      pairsCount: 0,
      weight: 55,
      color: "#C25140",
    },
    plate_45: {
      pairsCount: 12,
      weight: 45,
      color: "#00008B",
    },
    plate_35: {
      pairsCount: 1,
      weight: 35,
      color: "#CCCC00",
    },
    plate_25: {
      pairsCount: 1,
      weight: 25,
      color: "#5CB85C",
    },
    plate_10: {
      pairsCount: 2,
      weight: 10,
      color: "#FFFFFF",
    },
    plate_5: {
      pairsCount: 1,
      weight: 5,
      color: "#000000",
    },
    plate_2_5: {
      pairsCount: 1,
      weight: 2.5,
      color: "#000000",
    },
    plate_1_25: {
      pairsCount: 0,
      weight: 1.25,
      color: "#000000",
    },
    plate_0_5: {
      pairsCount: 0,
      weight: 0.5,
      color: "#000000",
    },
    plate_0_25: {
      pairsCount: 0,
      weight: 0.25,
      color: "#000000",
    },
  };
};

export const getDivisionResultsData = function (
  meet,
  lifters,
  division,
  weightClass,
  forecasted
) {
  const dataArray = [];
  let place = 1;
  dataArray.push({
    row: "title",
    name: (
      <span>
        <Link
          to={`/meets/${_.get(meet, "_id")}/division/${_.get(
            division,
            "_id"
          )}/weightClass/${_.get(weightClass, "_id")}`}
        >
          {division.name}
          <br />
          {weightClass.name}
        </Link>
      </span>
    ),
    weightClass,
    division,
    forecasted,
  });

  const divisionLifters = _.filter(lifters, (lifter) => {
    return !!_.find(lifter.divisions, (lifterDivision) => {
      if (lifterDivision.divisionId !== division._id) {
        return false;
      }
      const weightClassDoc = getLifterAwardsWeightClassDoc(
        meet,
        lifter,
        lifterDivision
      );
      return _.get(weightClassDoc, "_id") === weightClass._id;
    });
  });

  if (_.isEmpty(divisionLifters)) {
    return [];
  }

  const sortedLifters = _.orderBy(
    divisionLifters,
    [
      (lifter) => getScore(lifter, division, meet, forecasted),
      "bodyWeight",
      (lifter) => parseISO(getLastSuccessfulLiftTimestamp(lifter) || ""),
    ],
    ["desc", "asc", "asc"]
  );
  _.each(sortedLifters, (lifter) => {
    // lifters without a total do not get a place
    const total = getTotal(lifter, division._id, meet, forecasted);
    const displayPlace = total ? place : "";

    dataArray.push({
      ...lifter,
      division,
      weightClass,
      place: displayPlace,
      forecasted,
      lifterId: lifter._id,
    });
    place += 1;
  });
  dataArray.push(null);

  return dataArray;
};

export const isLbsMeet = function (meet) {
  return _.get(meet, "units") === "LBS";
};

export const isKgMeet = function (meet) {
  return _.get(meet, "units") !== "LBS";
};

export const getMeetUnits = function (meet) {
  return isKgMeet(meet) ? "kg" : "lbs";
};

export const unitedStatesMeet = function (meet) {
  if (!meet.federation) {
    return true;
  }

  if (
    _.includes(
      ["USAPL", "USPA", "USSF", "APA", "WRPF", "USSF", "100RAW", "OTHER"],
      meet.federation
    )
  ) {
    return true;
  }

  return false;
};

export const canadaMeet = function (meet) {
  if (_.includes(["CPU"], meet.federation)) {
    return true;
  }

  return false;
};

export const getBenchLabel = function (meet) {
  if (meet.federation === "USSF") {
    return "Press";
  }

  return "Bench";
};

export const getStateLabel = function (meet) {
  if (canadaMeet(meet)) {
    return "Province";
  }

  return "State";
};

export const isPaid = function (meet) {
  return (
    !meet.payment_status ||
    meet.payment_status === "FREE" ||
    meet.payment_status === "PAID"
  );
};

export const getDateFormat = function (meet) {
  return _.get(meet, "dateFormat", "MM/DD/YYYY") || "MM/DD/YYYY";
};
